import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import {getRating, convertRatingDecimalToDash, getSizeInPixels} from './utils/starRating';
import './StarRating.less';

const StarRating = ({rating, size, className, label, labelContext, starLabel, children}) => {
    const sizeVariant = getSizeInPixels(size);
    const inputVariant = (children) ? 'StarRatingInput' : 'StarRatingStatic';
    const inputSizeVariantClass = `${inputVariant}--${sizeVariant}`;

    const roundedRating = getRating(rating);
    const ratingClassSuffix = convertRatingDecimalToDash(roundedRating);
    const starClasses = classNames(`StarRating__stars StarRating__stars--${ratingClassSuffix}`, inputSizeVariantClass, `${inputVariant}`);
    const ariaLabel = starLabel.replace('{0}', roundedRating);
    const base = (
        <div role="img" aria-label={ariaLabel} className={starClasses}>
            {children}
        </div>
    );

    if (label) {
        return (
            <div className={`StarRating__label-container ${className}`}>
                <span className="sr-only">{labelContext}</span>
                {base}
                <span aria-hidden="true" className="StarRating__label">{label}</span>
            </div>
        );
    }

    return base;
};

StarRating.propTypes = {
    /** Raw Rating Number.  StarRating will round to the correct half star. */
    rating: PropTypes.number.isRequired,
    /** The StarRating size variation, one of: '12'|'16'|'24'|'32'. The value corresponds to the px height of the stars. 'sm' and 'lg' are deprecated and map to 12 and 24 respectively.  */
    size: PropTypes.oneOf(['12', '16', '24', '32', 'lg', 'sm']),
    /** Lingua translation used to build labels for each star in the group. Required for a11y. */
    starLabel: PropTypes.string,
    /** Vertically aligned text label that will render to the right of the rating stars. */
    label: PropTypes.string,
    /** Context for the label - this prop is only required if the optional label prop is used. Required for a11y. */
    labelContext(props, propName, componentName) {
        /* istanbul ignore next */
        if (props.labelContext && typeof props.labelContext !== 'string') {
            return new Error(`Failed prop type: Invalid prop ${propName} of type ${typeof props.labelContext} supplied to ${componentName}, expected \`string\`.`);
        }
        /* istanbul ignore next */
        if (props.label && !props.labelContext) {
            return new Error(
                `${propName} is required when using the optional label prop in ${componentName}.`
            );
        }
        return false;
    }
};

StarRating.defaultProps = {
    rating: 0,
    size: '16',
    starLabel: '{0} out of five stars'
};

export default StarRating;
